import { useEffect, useRef, useId, useCallback } from 'react';

import {
	Typography,
	FormGroup,
	FormControlLabel,
	Box,
	Grid,
	useMediaQuery,
	useTheme,
	DialogActions,
	DialogContent,
	Theme,
	LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useAppContext } from '@/context';
import { dataLayerConfig } from '@/utils';
import TAGButton from '@/components/TAGButton';
import TAGSvgIcon from '@/components/TAGSvgIcon/TAGSvgIcon';

import {
	getFormGroupStyles,
	getFormControlLabelStyles,
	getFormLabelBoxWrapper,
	getSelectedOptionIconStyles,
} from '../Quiz.styles';
import { IStateObject, TFormData, TOption, TQuestion, quizGA4DataLayerPush } from '../Quiz.helpers';
import QuestionDisclaimer from '../QuestionDisclaimer/QuestionDisclaimer';

const renderDisclaimer = (question: TQuestion, position: 'top' | 'bottom', isSmallScreen: boolean) => {
	if (!question.disclaimer) return null;

	const shouldRenderAtTop = position === 'top' && question.disclaimerPosition === 'top';
	const shouldRenderAtBottom =
		position === 'bottom' && (!question.disclaimerPosition || question.disclaimerPosition === 'bottom');

	if (!shouldRenderAtTop && !shouldRenderAtBottom) return null;

	return (
		<Grid item width="100%">
			<QuestionDisclaimer
				title={question.disclaimer.title}
				description={question.disclaimer.description}
				link={question.disclaimer.link}
				linkText={question.disclaimer.linkText}
				isSmallScreen={isSmallScreen}
			/>
		</Grid>
	);
};

export interface IQuestions {
	state: IStateObject;
	setState: (newState: Partial<IStateObject>) => void;
	showInDialogOrDrawer: boolean;
	isAutoAdvanceEnabled?: boolean;
	quizType?: string;
}

interface IQuestionsWrapper {
	questionToDisplay: TQuestion;
	isSmallScreen: boolean;
	formData: TFormData;
	fieldHasError: boolean;
	activeQuestionIndex: number;
	handleAnswerChange: (option: TOption) => void;
	theme: Theme;
	useNewQuizDesign: boolean;
	progress: number;
	questionGroupId: string;
}

interface IActionButtonsWrapper {
	isSmallScreen: boolean;
	activeQuestionIndex: number;
	handleBack: () => void;
	handleNext: () => void;
	useNewQuizDesign: boolean;
	isSpanishQuiz?: boolean;
}

interface IQuestionsAccessibilityWrapper {
	children: React.ReactNode;
	isAutoAdvanceEnabled: boolean;
	questionType: string;
	activeQuestionIndex: number;
	noOfQuestions: number;
}

const QuestionsWrapper = ({
	questionToDisplay,
	isSmallScreen,
	formData,
	fieldHasError,
	activeQuestionIndex,
	handleAnswerChange,
	theme,
	useNewQuizDesign,
	progress,
	questionGroupId,
}: IQuestionsWrapper) => {
	return (
		<Grid container direction="column" justifyContent="center" alignItems="center" gap={2}>
			<Grid container direction="column" justifyContent="center" alignItems="center" id={questionGroupId}>
				{useNewQuizDesign ? (
					<>
						<Typography
							variant={isSmallScreen ? 'bodySmallBook' : 'bodyMediumBook'}
							color="text.primary"
							sx={{ marginBottom: '1rem' }}
						>
							Question
						</Typography>
						<Grid container direction="row" justifyContent="center" alignItems="center">
							<Typography variant={'header3'} color="text.tertiary">
								<Typography component="span" variant={'header3'} color="text.primary">
									{activeQuestionIndex + 1}
								</Typography>{' '}
								/{' '}
								<Typography component="span" variant={'header3'} color="text.tertiary">
									{formData.form.form_questions.length}
								</Typography>
							</Typography>
						</Grid>
					</>
				) : (
					// Fallback for the old quiz design
					<Typography variant={isSmallScreen ? 'bodySmallBook' : 'bodyMediumBook'}>
						Question {activeQuestionIndex + 1} of {formData.form.form_questions.length}
					</Typography>
				)}
			</Grid>
			{useNewQuizDesign && (
				<Box sx={{ width: '100%', textAlign: 'center' }}>
					<LinearProgress
						variant="determinate"
						value={progress}
						sx={{ borderRadius: '.25rem', width: isSmallScreen ? '100%' : '21rem', margin: '0 auto' }}
					/>
				</Box>
			)}

			{/* Disclaimer image at top */}
			{renderDisclaimer(questionToDisplay, 'top', isSmallScreen)}

			<Grid item>
				<Typography variant="header2" color="text.primary" textAlign="center">
					{questionToDisplay.question}
				</Typography>
			</Grid>

			{questionToDisplay.description ? (
				<Grid item textAlign="center">
					<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>
						{questionToDisplay.description}
					</Typography>
				</Grid>
			) : null}

			{questionToDisplay.eyebrow ? (
				<Grid item>
					<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>
						{questionToDisplay.eyebrow}
					</Typography>
				</Grid>
			) : null}

			{fieldHasError ? (
				<Grid item>
					<Typography variant="notification" color="error.dark" data-testid="question_field_error_message">
						{questionToDisplay.validationMessage}
					</Typography>
				</Grid>
			) : null}

			<Grid item width="100%">
				<FormGroup sx={getFormGroupStyles} aria-labelledby={questionGroupId}>
					{questionToDisplay.options.map((option: TOption) => {
						const isOptionSelected =
							questionToDisplay.type === 'SingleAnswer'
								? questionToDisplay.answer === option
								: (questionToDisplay.answer as TOption[])?.some((answer) => answer === option);

						const selectedOptionFontVariant = isSmallScreen ? 'bodyMediumSemiBold' : 'bodyLargeSemiBold';
						const defaultOptionFontVariant = isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook';
						return (
							<FormControlLabel
								key={option.id}
								sx={getFormControlLabelStyles}
								labelPlacement="end"
								label=""
								control={
									<Box
										sx={getFormLabelBoxWrapper(theme, isOptionSelected, useNewQuizDesign)}
										onClick={() => handleAnswerChange(option)}
										onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
											if (e.code === 'Space') {
												handleAnswerChange(option);
											}
										}}
										component="div"
										tabIndex={0}
										data-testid={`option_${option.id}`}
										aria-selected={isOptionSelected}
										// Add auto-advance description for single answer questions
										aria-describedby={
											questionToDisplay.type === 'SingleAnswer' ? 'auto-advance-info' : undefined
										}
									>
										<Typography
											color={useNewQuizDesign ? 'text.primary' : 'text.interactive'}
											variant={
												isOptionSelected ? selectedOptionFontVariant : defaultOptionFontVariant
											}
											textAlign="center"
											fontWeight={isOptionSelected ? 500 : 300}
										>
											{option.value}
										</Typography>
										{isOptionSelected && questionToDisplay.type !== 'SingleAnswer' ? (
											<CheckCircleIcon color="secondary" sx={getSelectedOptionIconStyles} />
										) : null}
									</Box>
								}
							/>
						);
					})}
				</FormGroup>
			</Grid>
		</Grid>
	);
};

const ActionButtonsWrapper = ({
	isSmallScreen,
	activeQuestionIndex,
	handleBack,
	handleNext,
	useNewQuizDesign,
	isSpanishQuiz = false,
}: IActionButtonsWrapper) => {
	// Add button translations
	const getButtonText = (buttonType: string) => {
		if (!isSpanishQuiz) {
			return {
				back: 'Back',
				continue: useNewQuizDesign ? 'Continue' : 'Next',
			}[buttonType];
		}

		return {
			back: 'Atrás',
			continue: 'Continuar',
		}[buttonType];
	};

	return (
		<Grid
			container
			spacing={2}
			justifyContent="center"
			alignItems="center"
			direction={isSmallScreen ? 'column-reverse' : 'row'}
		>
			{activeQuestionIndex ? (
				<Grid item>
					<TAGButton
						size={isSmallScreen ? 'S' : 'M'}
						sx={{ width: isSmallScreen ? '100%' : 'auto', marginRight: isSmallScreen ? '0px' : '1rem' }}
						variant="tertiaryDefault"
						onClick={handleBack}
						dataTestId="questions_back_button"
						startIcon={<TAGSvgIcon icon={'ChevronLeftFunc'} size={25} />}
					>
						{getButtonText('back')}
					</TAGButton>
				</Grid>
			) : null}
			<Grid item sx={{ width: isSmallScreen ? '100%' : 'auto' }}>
				<TAGButton
					size={isSmallScreen ? 'S' : 'M'}
					sx={{ width: isSmallScreen ? '100%' : 'auto', marginLeft: isSmallScreen ? '' : '1rem' }}
					variant={useNewQuizDesign ? 'primaryDefault' : 'secondaryDefault'}
					endIcon={<TAGSvgIcon icon={'ChevronRightFunc'} size={25} />}
					onClick={handleNext}
					dataTestId="questions_next_button"
				>
					{getButtonText('continue')}
				</TAGButton>
			</Grid>
		</Grid>
	);
};

export default function Questions({
	state,
	setState,
	showInDialogOrDrawer = false,
	isAutoAdvanceEnabled = false,
	quizType,
}: Readonly<IQuestions>) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const isSpanishQuiz = quizType === 'ClearChoiceSpanish';
	const containerRef = useRef<HTMLDivElement>(null);
	const autoAdvanceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const previousQuestionIndexRef = useRef<number>(0);
	const answerClearedByNavigationRef = useRef<boolean>(false);
	const previousFormTypeRef = useRef<string>('Questions');
	const hasProcessedUserDetailsReturnRef = useRef<boolean>(false);
	const answerSelectionTriggerRef = useRef<number>(0);
	// Tracks whether the quiz-funnel entry event (form_step 'start') has fired. It
	// must fire once, when the user answers the FIRST question — not on quiz load
	// (DCP-14686 corrects DCP-14512, which emitted 'start' on quiz visibility).
	const hasEmittedStartRef = useRef<boolean>(false);
	const questionGroupId = `quiz-question-group-${useId()}`;

	const { activeQuestionIndex, formData, fieldHasError, formType } = state;

	const formQuestions = formData?.form?.form_questions || [];
	const noOfQuestions = formQuestions.length;
	let questionToDisplay = formQuestions[activeQuestionIndex];

	const { config, stateCodeFromHeaders } = useAppContext();
	const brandName = config.name;

	// Force clear the last question's answer when coming back from UserDetails
	const isLastQuestion = activeQuestionIndex === noOfQuestions - 1;
	const isComingFromUserDetails =
		formType === 'Questions' &&
		previousFormTypeRef.current === 'UserDetails' &&
		!hasProcessedUserDetailsReturnRef.current;

	if (isComingFromUserDetails && isLastQuestion && questionToDisplay?.answer) {
		// Create a modified version for display
		questionToDisplay = {
			...questionToDisplay,
			answer: questionToDisplay.type === 'MultiAnswer' ? [] : null,
		};

		setTimeout(() => {
			if (formData) {
				setState({
					fieldHasError: false,
					formData: {
						...formData,
						form: {
							...formData.form,
							form_questions: formData.form.form_questions.map(
								(formQuestion: TQuestion, index: number) => {
									if (index === activeQuestionIndex) {
										return {
											...formQuestion,
											answer: formQuestion.type === 'MultiAnswer' ? [] : null,
										};
									}
									return formQuestion;
								}
							),
						},
					},
				});
			}
		}, 0);

		hasProcessedUserDetailsReturnRef.current = true;
	}

	useEffect(() => {
		if (formType !== previousFormTypeRef.current) {
			if (formType === 'UserDetails') {
				hasProcessedUserDetailsReturnRef.current = false;
			}
			previousFormTypeRef.current = formType || 'Questions';
		}
	}, [formType]);

	// Clear answer when navigating back between questions
	useEffect(() => {
		const wasGoingBack = activeQuestionIndex < previousQuestionIndexRef.current;

		if (wasGoingBack && questionToDisplay && formData?.form) {
			answerClearedByNavigationRef.current = true;

			setState({
				formData: {
					...formData,
					form: {
						...formData.form,
						form_questions: formData.form.form_questions.map((formQuestion: TQuestion) => {
							if (formQuestion.question === questionToDisplay.question) {
								return {
									...formQuestion,
									answer: questionToDisplay.type === 'MultiAnswer' ? [] : null,
								};
							}
							return formQuestion;
						}),
					},
				},
			});

			setTimeout(() => {
				answerClearedByNavigationRef.current = false;
			}, 1000);
		}

		previousQuestionIndexRef.current = activeQuestionIndex;

		// Reset the processed flag when changing questions
		if (activeQuestionIndex !== noOfQuestions - 1) {
			hasProcessedUserDetailsReturnRef.current = false;
		}
	}, [activeQuestionIndex, questionToDisplay, setState, formData, noOfQuestions]);

	// Scroll to top when question changes
	useEffect(() => {
		const scrollToTop = () => {
			const questionElement = globalThis?.document?.getElementById?.(questionGroupId);
			if (questionElement) {
				questionElement.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
					inline: 'nearest',
				});
			}
		};

		setTimeout(scrollToTop, 100);
	}, [activeQuestionIndex, questionGroupId]);

	// Helper function to push data to dataLayer
	const pushToDataLayer = useCallback(() => {
		if (!formData) {
			return;
		}

		// Emit the funnel entry step once, when the user answers the FIRST question.
		// pushToDataLayer runs per advance in question order, so its first invocation
		// is Q1 — the moment the user has answered the first question. This precedes
		// the Q1 push below, so the funnel reads start -> Q1 (DCP-14686).
		if (!hasEmittedStartRef.current) {
			hasEmittedStartRef.current = true;
			quizGA4DataLayerPush(brandName, 'start', 'N/A', formData, stateCodeFromHeaders);
		}

		const isClearChoice = brandName === 'ClearChoice';

		if (isClearChoice) {
			const activeQuestionData = formData.form?.form_questions?.[activeQuestionIndex];

			const { answer } = activeQuestionData;

			let form_answer = 'N/A';

			if (answer) {
				if (Array.isArray(answer)) {
					form_answer = answer
						.map((selection) => {
							const selectionIndex = activeQuestionData.options.findIndex(
								(option) => option.id === selection.id
							);
							return selectionIndex > -1 ? ANSWER_ENCODINGS[selectionIndex] : null;
						})
						.filter((encoding): encoding is string => encoding !== null)
						.join(', ');
				} else {
					form_answer =
						ANSWER_ENCODINGS[activeQuestionData.options.findIndex((opt) => opt.id === answer.id) ?? -1] ||
						'N/A';
				}
			}

			dataLayerConfig({
				event: 'formSubmission',
				form_data: {
					brand: formData.form.brand,
					product_line: formData.form.product_line,
					submission_date: new Date().toISOString(),
					form_title: formData.form.form_title,
					form_description: formData.form.form_description,
					form_type: formData.form.form_type,
					form_result: 'N/A',
					form_answer,
					form_step: `Q${activeQuestionIndex + 1}`,
				},
			});
		} else {
			// datalayer push for all other brands
			quizGA4DataLayerPush(brandName, `Q${activeQuestionIndex + 1}`, 'N/A', formData, stateCodeFromHeaders);
		}
	}, [activeQuestionIndex, brandName, formData, stateCodeFromHeaders]);

	// Auto-advance logic with scroll
	const handleAutoAdvance = useCallback(() => {
		if (!isAutoAdvanceEnabled) {
			return;
		}

		// Clear any existing timeout
		if (autoAdvanceTimeoutRef.current) {
			clearTimeout(autoAdvanceTimeoutRef.current);
		}

		autoAdvanceTimeoutRef.current = setTimeout(() => {
			pushToDataLayer();

			if (activeQuestionIndex + 1 === noOfQuestions) {
				setState({
					formType: 'UserDetails',
					fieldHasError: false,
				});

				announceToScreenReader('All questions completed. Moving to contact details form.');
			} else {
				setState({
					activeQuestionIndex: activeQuestionIndex + 1,
					fieldHasError: false,
				});

				announceToScreenReader(`Moving to question ${activeQuestionIndex + 2} of ${noOfQuestions}`);
			}
		}, 250);
	}, [isAutoAdvanceEnabled, activeQuestionIndex, noOfQuestions, setState, pushToDataLayer]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (autoAdvanceTimeoutRef.current) {
				clearTimeout(autoAdvanceTimeoutRef.current);
			}
		};
	}, []);

	// Trigger auto-advance when answer changes and auto-advance is enabled
	useEffect(() => {
		// Only auto-advance for SingleAnswer questions
		if (!questionToDisplay || !isAutoAdvanceEnabled || questionToDisplay.type !== 'SingleAnswer') {
			return;
		}

		const hasAnswer = questionToDisplay.answer !== null && questionToDisplay.answer !== undefined;

		if (hasAnswer && !answerClearedByNavigationRef.current && !isComingFromUserDetails) {
			handleAutoAdvance();
		}
	}, [
		questionToDisplay.answer,
		isAutoAdvanceEnabled,
		handleAutoAdvance,
		questionToDisplay.type,
		questionToDisplay,
		isComingFromUserDetails,
	]);

	if (!formData) {
		return null;
	}

	// Helper function for MultiAnswer logic
	const handleMultiAnswerChange = (
		formQuestion: TQuestion,
		selectedAnswer: TOption[] | TOption | null,
		option: TOption
	): TOption[] => {
		if (Array.isArray(selectedAnswer) && selectedAnswer.some((answer) => answer.id === option.id)) {
			// Remove the option if it's already selected
			return selectedAnswer.filter((answer: TOption) => answer.id !== option.id);
		}

		// Add the option
		if (option.value === 'None of the above') {
			return [option];
		}

		const currentAnswers = Array.isArray(selectedAnswer) ? selectedAnswer : [];
		return [...currentAnswers, option].filter((answer: TOption) => answer.value !== 'None of the above');
	};

	// Helper function for SingleAnswer logic
	const handleSingleAnswerChange = (
		selectedAnswer: TOption | null,
		option: TOption,
		isAutoAdvanceEnabled: boolean,
		questionType: string,
		answerSelectionTriggerRef: React.MutableRefObject<number>
	): TOption | null => {
		const newAnswer = (selectedAnswer as TOption)?.id === option.id ? null : option;

		// Trigger auto-advance for SingleAnswer questions when an answer is selected
		if (newAnswer && isAutoAdvanceEnabled && questionType === 'SingleAnswer') {
			answerSelectionTriggerRef.current += 1;
		}

		return newAnswer;
	};

	// Helper function to update form questions
	const updateFormQuestion = (
		formQuestion: TQuestion,
		questionToDisplay: TQuestion,
		option: TOption,
		isAutoAdvanceEnabled: boolean,
		answerSelectionTriggerRef: React.MutableRefObject<number>
	): TQuestion => {
		if (formQuestion.question !== questionToDisplay.question) {
			return formQuestion;
		}

		const selectedAnswer = formQuestion.answer;

		if (questionToDisplay.type === 'MultiAnswer') {
			return {
				...formQuestion,
				answer: handleMultiAnswerChange(formQuestion, selectedAnswer, option),
			};
		}

		return {
			...formQuestion,
			answer: handleSingleAnswerChange(
				selectedAnswer as TOption | null,
				option,
				isAutoAdvanceEnabled,
				questionToDisplay.type,
				answerSelectionTriggerRef
			),
		};
	};

	const handleAnswerChange = (option: TOption) => {
		// Reset the UserDetails return processing flag when user makes a selection
		hasProcessedUserDetailsReturnRef.current = true;

		setState({
			fieldHasError: false,
			formData: {
				form: {
					...formData.form,
					form_questions: formData.form.form_questions.map((formQuestion: TQuestion) =>
						updateFormQuestion(
							formQuestion,
							questionToDisplay,
							option,
							isAutoAdvanceEnabled,
							answerSelectionTriggerRef
						)
					),
				},
			},
		});
	};

	const handleNext = () => {
		const fieldHasError =
			(questionToDisplay.type === 'SingleAnswer' && !questionToDisplay.answer) ||
			(questionToDisplay.type === 'MultiAnswer' && !(questionToDisplay.answer as TOption[])?.length);
		if (fieldHasError) {
			setState({
				fieldHasError: true,
			});
			return;
		}

		pushToDataLayer();

		if (activeQuestionIndex + 1 === noOfQuestions) {
			setState({
				formType: 'UserDetails',
				fieldHasError: false,
			});
			return;
		}

		setState({
			activeQuestionIndex: activeQuestionIndex + 1,
			fieldHasError: false,
		});
	};

	const handleBack = () => {
		setState({
			fieldHasError: false,
			activeQuestionIndex: activeQuestionIndex - 1,
		});
	};

	const totalQuestions = formData.form.form_questions.length;
	const progress = totalQuestions > 0 ? ((activeQuestionIndex + 1) / totalQuestions) * 100 : 0;

	// render as dialog or drawer
	if (showInDialogOrDrawer) {
		return (
			<>
				<DialogContent ref={containerRef}>
					<QuestionsAccessibilityWrapper
						isAutoAdvanceEnabled={isAutoAdvanceEnabled}
						questionType={questionToDisplay?.type}
						activeQuestionIndex={activeQuestionIndex}
						noOfQuestions={noOfQuestions}
					>
						<QuestionsWrapper
							questionToDisplay={questionToDisplay}
							isSmallScreen={isSmallScreen}
							formData={formData}
							fieldHasError={fieldHasError}
							activeQuestionIndex={activeQuestionIndex}
							handleAnswerChange={handleAnswerChange}
							theme={theme}
							useNewQuizDesign={config.featureFlags.featureList.includes('useNewQuizDesign')}
							progress={progress}
							questionGroupId={questionGroupId}
						/>
					</QuestionsAccessibilityWrapper>
				</DialogContent>
				<DialogActions sx={{ margin: '.75rem' }}>
					<ActionButtonsWrapper
						isSmallScreen={isSmallScreen}
						activeQuestionIndex={activeQuestionIndex}
						handleBack={handleBack}
						handleNext={handleNext}
						useNewQuizDesign={config.featureFlags.featureList.includes('useNewQuizDesign')}
						isSpanishQuiz={isSpanishQuiz}
					/>
					{/* Disclaimer image at bottom */}
					{renderDisclaimer(questionToDisplay, 'bottom', isSmallScreen)}
				</DialogActions>
			</>
		);
	}

	// render as a section in a page
	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				minHeight: '100%',
				justifyContent: 'space-between',
			}}
		>
			<div ref={containerRef} style={{ flex: 1 }}>
				<QuestionsAccessibilityWrapper
					isAutoAdvanceEnabled={isAutoAdvanceEnabled}
					questionType={questionToDisplay?.type}
					activeQuestionIndex={activeQuestionIndex}
					noOfQuestions={noOfQuestions}
				>
					<QuestionsWrapper
						questionToDisplay={questionToDisplay}
						isSmallScreen={isSmallScreen}
						formData={formData}
						fieldHasError={fieldHasError}
						activeQuestionIndex={activeQuestionIndex}
						handleAnswerChange={handleAnswerChange}
						theme={theme}
						useNewQuizDesign={config.featureFlags.featureList.includes('useNewQuizDesign')}
						progress={progress}
						questionGroupId={questionGroupId}
					/>
				</QuestionsAccessibilityWrapper>
			</div>

			{/* Always show navigation buttons */}
			<Box sx={{ marginTop: 'auto', paddingTop: 4 }}>
				<ActionButtonsWrapper
					isSmallScreen={isSmallScreen}
					activeQuestionIndex={activeQuestionIndex}
					handleBack={handleBack}
					handleNext={handleNext}
					useNewQuizDesign={config.featureFlags.featureList.includes('useNewQuizDesign')}
					isSpanishQuiz={isSpanishQuiz}
				/>
				{/* Disclaimer image at bottom */}
				{renderDisclaimer(questionToDisplay, 'bottom', isSmallScreen)}
			</Box>
		</Box>
	);
}

// Enhanced accessibility wrapper for the questions
const QuestionsAccessibilityWrapper = ({
	children,
	isAutoAdvanceEnabled,
	questionType,
	activeQuestionIndex,
	noOfQuestions,
}: IQuestionsAccessibilityWrapper) => {
	const autoAdvanceMessage =
		isAutoAdvanceEnabled && questionType === 'SingleAnswer'
			? 'This question will automatically advance after you make a selection.'
			: '';

	const srOnlyStyles = {
		position: 'absolute' as const,
		left: '-9999px',
		width: '1px',
		height: '1px',
		overflow: 'hidden',
		clip: 'rect(0, 0, 0, 0)',
		whiteSpace: 'nowrap' as const,
		border: 0,
		padding: 0,
		margin: 0,
	};

	return (
		<form
			aria-label={`Quiz questions. Question ${
				activeQuestionIndex + 1
			} of ${noOfQuestions}. ${autoAdvanceMessage}`}
			onSubmit={(e) => e.preventDefault()} // Prevent default form submission
		>
			{/* Progress indicator for screen readers */}
			<output aria-live="polite" aria-atomic="false" style={srOnlyStyles}>
				Question {activeQuestionIndex + 1} of {noOfQuestions}
			</output>

			{/* Auto-advance notification for screen readers */}
			{isAutoAdvanceEnabled && (
				<>
					<Box id="auto-advance-info" sx={srOnlyStyles} role="note" aria-label="Auto-advance information">
						{questionType === 'SingleAnswer'
							? 'Auto-advance is enabled. Selecting an answer will automatically move to the next question after 1.5 seconds.'
							: 'Auto-advance is enabled for single-answer questions only. Multiple-choice questions require clicking the Next button.'}
					</Box>

					{/* Live region for auto-advance announcements */}
					<output
						aria-live="assertive"
						aria-atomic="true"
						style={srOnlyStyles}
						id="auto-advance-announcements"
					>
						{/* This will be populated by the announceToScreenReader function */}
					</output>
				</>
			)}

			{children}
		</form>
	);
};

/**
 * @NOTE
 *
 * these are used to represent which selections were made in the quiz
 * without including any personal information. View datalayer guide
 * included in: https://aspendent.atlassian.net/browse/DCP-4101
 * */
const ANSWER_ENCODINGS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'K'];

// WCAG compliant screen reader announcement function
const announceToScreenReader = (message: string) => {
	// Try to use existing live region first
	const existingAnnouncement = document.getElementById('auto-advance-announcements');

	if (existingAnnouncement) {
		existingAnnouncement.textContent = message;
		// Clear after announcement
		setTimeout(() => {
			existingAnnouncement.textContent = '';
		}, 1000);
	} else {
		// Fallback to creating new element with clean styles
		const announcement = document.createElement('output');
		announcement.setAttribute('aria-live', 'assertive');
		announcement.setAttribute('aria-atomic', 'true');
		announcement.style.position = 'absolute';
		announcement.style.left = '-9999px';
		announcement.style.width = '1px';
		announcement.style.height = '1px';
		announcement.style.overflow = 'hidden';
		announcement.style.clipPath = 'inset(50%)';
		announcement.style.whiteSpace = 'nowrap';
		announcement.style.border = '0';
		announcement.style.padding = '0';
		announcement.style.margin = '0';
		announcement.textContent = message;

		document.body.appendChild(announcement);

		// Clean up after announcement
		setTimeout(() => {
			if (announcement.parentNode) {
				document.body.removeChild(announcement);
			}
		}, 1000);
	}
};
